import { useState } from "react";
import { toast } from "sonner";
import { Crown, Search, CheckCircle2, XCircle } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { money } from "@/lib/format";

const Pill = ({ ok, children }) => (
  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${ok ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" : "bg-slate-700/40 text-slate-400 border border-slate-600/40"}`}>
    {children}
  </span>
);

const RankTag = ({ r, name }) => (
  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${r >= 1 ? "bg-amber-500/15 text-amber-300 border border-amber-500/30" : "bg-slate-700/40 text-slate-400"}`}>
    {r >= 1 ? `R${r} · ${name}` : "Unranked"}
  </span>
);

export default function AdminRoyalty() {
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);

  const check = async () => {
    if (!username.trim()) { toast.error("Enter a username"); return; }
    setLoading(true); setRes(null);
    try {
      const { data } = await api.get(`/admin/royalty-diagnostic?username=${encodeURIComponent(username.trim())}`);
      setRes(data);
    } catch (err) {
      toast.error(apiError(err?.response?.data?.detail) || "Lookup failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-4xl space-y-6" data-testid="admin-royalty-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/30 grid place-items-center">
          <Crown className="w-5 h-5 text-amber-400" />
        </div>
        <div>
          <h1 className="font-display font-extrabold text-xl">Royalty Diagnostic</h1>
          <p className="text-sm text-slate-400">Check any member: their leg ranks, whether they qualify, and today's royalty base.</p>
        </div>
      </div>

      <div className="rounded-2xl bg-[#0D0E12] border border-amber-500/15 p-5 flex gap-3">
        <input
          data-testid="royalty-username-input"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && check()}
          placeholder="Enter username (or email / referral code)"
          className="flex-1 bg-[#08080C] border border-amber-500/20 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-500/50"
        />
        <button data-testid="royalty-check-btn" onClick={check} disabled={loading}
          className="inline-flex items-center gap-2 gold-btn px-5 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50">
          <Search className="w-4 h-4" /> {loading ? "Checking…" : "Check"}
        </button>
      </div>

      {res && (
        <div className="space-y-4" data-testid="royalty-result">
          <div className={`rounded-2xl border p-5 ${res.qualifies ? "bg-emerald-500/5 border-emerald-500/25" : "bg-[#0D0E12] border-amber-500/15"}`}>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <span className="font-display font-bold text-lg">@{res.username}</span>
                {res.qualifies
                  ? <Pill ok><CheckCircle2 className="w-3.5 h-3.5" /> Qualifies</Pill>
                  : <Pill><XCircle className="w-3.5 h-3.5" /> Not qualified</Pill>}
              </div>
              {res.qualifies && (
                <div className="text-right">
                  <div className="text-xs text-slate-400 uppercase">Royalty rate</div>
                  <div className="font-mono-fin text-2xl font-extrabold gold-text">{res.royalty_pct}%</div>
                </div>
              )}
            </div>
            <p className="text-sm text-slate-300 mt-3" data-testid="royalty-reason">{res.reason}</p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="rounded-2xl bg-[#0D0E12] border border-amber-500/15 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-semibold text-sky-300">Left leg</div>
                <div className="text-xs text-slate-400">{res.left_size} members</div>
              </div>
              <div className="text-xs text-slate-400 mb-2">Top rank: <RankTag r={res.left_top.rank} name={res.left_top.name} /></div>
              <div className="space-y-1.5 max-h-48 overflow-auto">
                {res.left_ranked.length ? res.left_ranked.map((m, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-slate-300">@{m.username}</span><RankTag r={m.rank} name={m.rank_name} />
                  </div>
                )) : <div className="text-xs text-slate-500">No ranked members in this leg.</div>}
              </div>
            </div>

            <div className="rounded-2xl bg-[#0D0E12] border border-amber-500/15 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-semibold text-amber-300">Right leg</div>
                <div className="text-xs text-slate-400">{res.right_size} members</div>
              </div>
              <div className="text-xs text-slate-400 mb-2">Top rank: <RankTag r={res.right_top.rank} name={res.right_top.name} /></div>
              <div className="space-y-1.5 max-h-48 overflow-auto">
                {res.right_ranked.length ? res.right_ranked.map((m, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-slate-300">@{m.username}</span><RankTag r={m.rank} name={m.rank_name} />
                  </div>
                )) : <div className="text-xs text-slate-500">No ranked members in this leg.</div>}
              </div>
            </div>
          </div>

          <div className="rounded-2xl bg-[#0D0E12] border border-amber-500/15 p-5">
            <div className="grid sm:grid-cols-3 gap-4 mb-4">
              <div><div className="text-xs text-slate-400 uppercase">Qualifying rank</div><div className="mt-1"><RankTag r={res.qualifying_rank} name={res.qualifying_name} /></div></div>
              <div><div className="text-xs text-slate-400 uppercase">Today's base (non-ROI)</div><div className="font-mono-fin text-xl font-bold mt-1">{money(res.today_base)}</div></div>
              <div><div className="text-xs text-slate-400 uppercase">Today's royalty</div><div className="font-mono-fin text-xl font-bold gold-text mt-1">{money(res.today_royalty)}</div></div>
            </div>
            <div className="text-xs text-slate-500 mb-2">Base = today's Level + Matching + Salary + Turnover income of qualifying achievers ({res.today}). ROI is excluded.</div>
            {res.contributors?.length > 0 && (
              <div className="border-t border-amber-500/10 pt-3 space-y-1.5 max-h-56 overflow-auto">
                <div className="text-xs font-semibold text-slate-300 mb-1">Contributing achievers today</div>
                {res.contributors.map((c, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-slate-300">@{c.username} <span className="text-xs text-slate-500">({c.side})</span></span>
                    <span className="flex items-center gap-3"><RankTag r={c.rank} name={c.rank_name} /><span className="font-mono-fin text-slate-200">{money(c.today_income)}</span></span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
